import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Token as TokenIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import StatCard from "./StatCard";
import CostDetails from "./CostDetails";
import { formatNumber, formatCurrency, formatTokens } from "./validation";
import { useAuth } from "../../../hooks/AuthProvider";
import { AI_REPORT_TOKEN_USAGE } from "../../../config/urls";

const TokenUsageTab = ({ startDate, endDate }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState({
    total_tokens: 0,
    total_cost: 0,
    average_tokens_per_request: 0,
    monthly_cost_projection: 0,
  });

  const [trendData, setTrendData] = useState([]);
  const [costData, setCostData] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getValidToken();

      const response = await fetch(
        `${AI_REPORT_TOKEN_USAGE}?from_date=${startDate}&to_date=${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch token usage");

      const json = await response.json();
      if (!json.success) throw new Error("API returned failure");

      const data = json.data;

      setStats({
        total_tokens: data.total_tokens,
        total_cost: data.total_cost,
        average_tokens_per_request: data.average_tokens_per_request,
        monthly_cost_projection: data.monthly_cost_projection,
      });

      // Merge token + cost per day for charts; a day can have cost but no customer
      // tokens (knowledge-base training only).
      const tokensByDay = Object.fromEntries(data.token_usage_per_day.map((d) => [d.date, d.tokens]));
      const costByDay = Object.fromEntries(data.cost_per_day.map((d) => [d.date, d.cost]));
      const days = Array.from(new Set([...Object.keys(tokensByDay), ...Object.keys(costByDay)])).sort();
      const mergedTrend = days.map((date) => ({
        date,
        tokens: tokensByDay[date] || 0,
        // null = not converted yet: no bar rather than a zero one.
        cost: costByDay[date] ?? 0,
      }));

      setTrendData(mergedTrend);
      setCostData(data);
    } catch (err) {
      console.error(err);
      setError("Unable to load token usage analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  const currency = costData?.currency;
  const money = (value) => formatCurrency(value, currency);

  return (
    <Box>
      {/* ---- Stats ---- */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TokenIcon sx={{ color: "#06b6d4" }} />}
            label="Total Tokens"
            value={formatTokens(stats.total_tokens)}
            color="info"
            description="Combined number of input and output tokens processed by the AI."
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MoneyIcon sx={{ color: "#ffffff" }} />}
            label="Total Cost"
            value={money(stats.total_cost)}
            color="error"
            valueColor="error.main"
            description="Sum of the recorded cost of every AI call in this period, at each model's list price that day."
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<AssessmentIcon sx={{ color: "#ffffff"}} />}
            label="Avg Tokens / Request"
            value={formatNumber(stats.average_tokens_per_request)}
            color="purple"
            description="Average number of input and output tokens used per AI request."
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingIcon sx={{ color: "#ffffff" }} />}
            label="Monthly Projection"
            value={money(stats.monthly_cost_projection)}
            color="warning"
            description="Average daily recorded cost in this period × 30."
          />
        </Grid>
      </Grid>

      {/* ---- Token Trend ---- */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Token Usage Trend
        </Typography>
        <Box sx={{ height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis 
                tickFormatter={(value) => formatTokens(value)}
              />
              <Tooltip 
                formatter={(value) => [formatTokens(value), "Tokens"]}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="tokens"
                stroke="#1e3c72"
                strokeWidth={2}
                name="Tokens Used"
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* ---- Cost Trend ---- */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Cost Analysis by Day
        </Typography>
        <Box sx={{ height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis tickFormatter={(value) => money(value)} width={80} />
              <Tooltip
                formatter={(value) => [money(value), "Daily Cost"]}
              />
              <Legend />
              <Bar dataKey="cost" fill="#dc2626" name="Daily Cost" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <CostDetails data={costData} currency={currency} />
    </Box>
  );
};

export default TokenUsageTab;
